/**
 * service接口配置
 */
import type { ApiEndpoint } from '../types';
import { API_CATEGORIES } from '../constants';

export const serviceEndpoints: ApiEndpoint[] = [
  {
    id: "service-list",
    method: "GET",
    path: "/api/services",
    name: "获取微服务列表",
    description: "分页查询微服务列表，支持多条件搜索",
    category: API_CATEGORIES.SERVICE,
    queryParams: [
      { name: "current", type: "number", required: false, description: "当前页码", example: "1" },
      { name: "limit", type: "number", required: false, description: "每页条数", example: "10" },
      { name: "search", type: "string", required: false, description: "服务名称搜索" },
      { name: "status", type: "string", required: false, description: "服务状态（1-正常，0-异常）" },
      { name: "requires_auth", type: "string", required: false, description: "认证要求（true/false）" },
      { name: "is_external", type: "string", required: false, description: "服务类型（true-外部，false-内部）" },
      { name: "is_menu_item", type: "string", required: false, description: "菜单显示（true/false）" },
      { name: "api_token_status", type: "string", required: false, description: "Token状态（configured/not_configured）" },
      { name: "token_expiry_status", type: "string", required: false, description: "Token过期状态（expired/expires_soon/not_expired）" },
      { name: "health_status", type: "string", required: false, description: "健康状态（passed/failed）" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.services", type: "array", description: "微服务列表" },
      { name: "data.pagination", type: "object", description: "分页信息" },
      { name: "data.services[].id", type: "string", description: "微服务ID" },
      { name: "data.services[].serviceName", type: "string", description: "微服务名称" },
      { name: "data.services[].displayName", type: "string", description: "显示名称" },
      { name: "data.services[].serviceDescription", type: "string", description: "服务描述" },
      { name: "data.services[].serviceEndpoint", type: "string", description: "前端访问地址" },
      { name: "data.services[].serviceUrl", type: "string", description: "后端API地址" },
      { name: "data.services[].healthStatus", type: "boolean", description: "健康状态" },
      { name: "data.services[].status", type: "number", description: "服务状态" },
      { name: "data.services[].isActive", type: "boolean", description: "是否启用" },
      { name: "data.services[].hasToken", type: "boolean", description: "是否有Token" },
      { name: "data.services[].registered_operations", type: "number", description: "注册操作数量" },
      { name: "data.services[].allowed_audience", type: "array", description: "令牌受众列表" },
    ],
    requiredPermission: "msadmin:services:view",
  },
  {
    id: "service-all",
    method: "GET",
    path: "/api/services/all",
    name: "获取所有微服务",
    description: "获取所有微服务（不分页）",
    category: API_CATEGORIES.SERVICE,
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.items", type: "array", description: "微服务列表" },
      { name: "data.total", type: "number", description: "总数量" },
    ],
    requiredPermission: "msadmin:services:view",
  },
  {
    id: "service-detail",
    method: "GET",
    path: "/api/services/:id",
    name: "获取微服务详情",
    description: "根据ID获取微服务详细信息",
    category: API_CATEGORIES.SERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "微服务ID" },
      { name: "data.serviceName", type: "string", description: "微服务名称" },
      { name: "data.displayName", type: "string", description: "显示名称" },
      { name: "data.serviceDescription", type: "string", description: "服务描述" },
      { name: "data.serviceEndpoint", type: "string", description: "前端访问地址" },
      { name: "data.serviceUrl", type: "string", description: "后端API地址" },
      { name: "data.healthCheckUrl", type: "string", description: "健康检查地址" },
      { name: "data.healthStatus", type: "boolean", description: "健康状态" },
      { name: "data.status", type: "number", description: "服务状态" },
      { name: "data.isActive", type: "boolean", description: "是否启用" },
      { name: "data.hasToken", type: "boolean", description: "是否有Token" },
      { name: "data.tokenInfo", type: "object", description: "Token信息" },
      { name: "data.allowed_audience", type: "array", description: "令牌受众列表" },
    ],
    requiredPermission: "msadmin:services:view",
  },
  {
    id: "service-create",
    method: "POST",
    path: "/api/services",
    name: "创建微服务",
    description: "创建新的微服务",
    category: API_CATEGORIES.SERVICE,
    bodyParams: [
      { name: "service_name", type: "string", required: true, description: "微服务名称" },
      { name: "service_endpoint", type: "string", required: false, description: "前端访问地址" },
      { name: "service_url", type: "string", required: false, description: "后端API地址" },
      { name: "service_description", type: "string", required: false, description: "服务描述" },
      { name: "health_check_url", type: "string", required: false, description: "健康检查地址" },
      { name: "display_name", type: "string", required: false, description: "显示名称" },
      { name: "icon_name", type: "string", required: false, description: "图标名称" },
      { name: "route_path", type: "string", required: false, description: "路由路径" },
      { name: "menu_order", type: "number", required: false, description: "菜单顺序" },
      { name: "is_menu_item", type: "boolean", required: false, description: "是否显示在菜单" },
      { name: "requires_auth", type: "boolean", required: false, description: "是否需要认证" },
      { name: "is_external", type: "boolean", required: false, description: "是否外部服务" },
      { name: "is_system_service", type: "boolean", required: false, description: "是否系统服务" },
      { name: "status", type: "number", required: false, description: "服务状态" },
      { name: "allowed_audience", type: "array", required: false, description: "令牌受众列表", example: "[\"msspace\", \"msmq\"]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "新创建的微服务ID" },
      { name: "data.serviceName", type: "string", description: "微服务名称" },
      { name: "data.displayName", type: "string", description: "显示名称" },
    ],
    requiredPermission: "msadmin:services:create",
  },
  {
    id: "service-update",
    method: "PUT",
    path: "/api/services/:id",
    name: "更新微服务",
    description: "更新微服务信息",
    category: API_CATEGORIES.SERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    bodyParams: [
      { name: "service_name", type: "string", required: false, description: "微服务名称" },
      { name: "service_endpoint", type: "string", required: false, description: "前端访问地址" },
      { name: "service_url", type: "string", required: false, description: "后端API地址" },
      { name: "service_description", type: "string", required: false, description: "服务描述" },
      { name: "health_check_url", type: "string", required: false, description: "健康检查地址" },
      { name: "display_name", type: "string", required: false, description: "显示名称" },
      { name: "icon_name", type: "string", required: false, description: "图标名称" },
      { name: "route_path", type: "string", required: false, description: "路由路径" },
      { name: "menu_order", type: "number", required: false, description: "菜单顺序" },
      { name: "is_menu_item", type: "boolean", required: false, description: "是否显示在菜单" },
      { name: "requires_auth", type: "boolean", required: false, description: "是否需要认证" },
      { name: "is_external", type: "boolean", required: false, description: "是否外部服务" },
      { name: "required_permissions", type: "array", required: false, description: "所需权限列表" },
      { name: "allowed_audience", type: "array", required: false, description: "令牌受众列表", example: "[\"msspace\", \"msmq\"]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:services:update",
  },
  {
    id: "service-delete",
    method: "DELETE",
    path: "/api/services/:id",
    name: "删除微服务",
    description: "根据ID删除微服务",
    category: API_CATEGORIES.SERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "删除的微服务ID" },
      { name: "data.deletedStats", type: "object", description: "删除统计信息" },
    ],
    requiredPermission: "msadmin:services:delete",
  },
  {
    id: "service-health-check",
    method: "POST",
    path: "/api/services/:id/health-check",
    name: "微服务健康检查",
    description: "检查指定微服务的健康状态",
    category: API_CATEGORIES.SERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "微服务ID" },
      { name: "data.healthStatus", type: "boolean", description: "健康状态" },
      { name: "data.responseTime", type: "number", description: "响应时间（毫秒）" },
      { name: "data.lastCheck", type: "string", description: "检查时间" },
      { name: "data.healthCheckUrl", type: "string", description: "健康检查地址" },
      { name: "data.errorMessage", type: "string", description: "错误信息" },
    ],
    requiredPermission: "msadmin:services:health-check",
  },
  {
    id: "service-update-status",
    method: "PUT",
    path: "/api/services/:id/status",
    name: "更新微服务状态",
    description: "更新微服务的状态（启用/禁用）",
    category: API_CATEGORIES.SERVICE,
    pathParams: [{ name: "id", type: "string", required: true, description: "微服务ID" }],
    bodyParams: [{ name: "status", type: "number", required: true, description: "服务状态（1-启用，2-禁用，3-维护）" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:services:update",
  },
];
